import { useSearchParams, Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { newsList } from "@/mocks/news";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 4;
const tabs = ["全部", "新闻", "专题", "评论"] as const;

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get("category") || "全部";
  const currentPage = Math.max(1, Number(searchParams.get("page") || "1"));

  useSeo({
    title: "影视资讯 - 蜜桃成熟时电影",
    description:
      "蜜桃成熟时电影影视资讯频道，汇总系列重映、修复、新作拍摄动态与影评专题，帮助影迷及时了解相关影视动态。",
    keywords: "影视资讯,蜜桃成熟时资讯,电影新闻,电影专题,影评",
  });

  const filtered = newsList
    .filter((item) => activeTab === "全部" || item.category === activeTab)
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "全部") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") {
      next.delete("page");
    }
    setSearchParams(next);
  };

  const recommended = movies.slice(0, 3);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-7 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />
          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            影视资讯
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            重映与修复进展、新作拍摄动态、幕后访谈与类型研究。以专题和评论的方式，持续记录系列相关的每一个节点。
          </p>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-[1240px] gap-10 px-4 py-8 md:px-6 md:py-12 lg:grid-cols-[1.7fr_1fr] lg:gap-12">
        <div className="min-w-0">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex w-fit items-center gap-1 rounded-full bg-background-100 p-1">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => updateParam("category", tab)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    activeTab === tab
                      ? "bg-background-50 font-medium text-accent-700"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
            <p className="text-xs text-foreground-500">
              共 <span className="font-medium text-accent-600">{filtered.length}</span> 篇内容
            </p>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="flex flex-col gap-4">
                {pageItems.map((item) => (
                  <article
                    key={item.id}
                    className="group overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-colors hover:border-primary-300"
                  >
                    <Link to={`/news/${item.id}`} className="flex flex-col sm:flex-row">
                      <div className="h-[180px] w-full shrink-0 overflow-hidden bg-background-200 sm:h-auto sm:w-[220px]">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 影视资讯`}
                          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 p-4 md:p-5">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-400">
                          <span className="rounded-full bg-accent-100 px-2 py-0.5 text-accent-700">
                            {item.category}
                          </span>
                          <span>{item.date}</span>
                          <span className="h-3 w-px bg-background-300" />
                          <span>{item.author}</span>
                        </div>
                        <h2 className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-accent-600 md:text-lg">
                          {item.title}
                        </h2>
                        <p className="line-clamp-2 text-xs leading-relaxed text-foreground-500">
                          {item.summary}
                        </p>
                        <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-accent-600">
                          阅读全文
                          <i className="ri-arrow-right-line" />
                        </span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => updateParam("page", String(safePage - 1))}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line" />
                  上一页
                </button>
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => updateParam("page", String(index + 1))}
                    className={`h-8 w-8 rounded-md text-xs transition-colors ${
                      safePage === index + 1
                        ? "bg-accent-500 text-background-50"
                        : "border border-background-300 text-foreground-600 hover:border-primary-400"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={safePage >= totalPages}
                  onClick={() => updateParam("page", String(safePage + 1))}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  下一页
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-newspaper-line text-xl" />
              </span>
              <h2 className="font-heading text-base font-semibold text-foreground-900">该分类暂无内容</h2>
              <p className="max-w-sm text-xs leading-relaxed text-foreground-500">
                可以先浏览其他分类，或回到全部资讯查看最近更新。
              </p>
              <button
                type="button"
                onClick={() => setSearchParams({})}
                className="mt-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            </div>
          )}
        </div>

        <aside className="min-w-0">
          <div className="rounded-lg bg-secondary-100 p-5">
            <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
              <i className="ri-price-tag-3-line text-accent-600" />
              热门标签
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                { label: "经典系列", to: "/category?tag=经典系列" },
                { label: "最新上映", to: "/category?tag=最新上映" },
                { label: "修复重映", to: "/news?category=专题" },
                { label: "演员访谈", to: "/actors" },
                { label: "类型研究", to: "/news?category=评论" },
                { label: "幕后", to: "/news?category=专题" },
              ].map((tag) => (
                <Link
                  key={tag.label}
                  to={tag.to}
                  className="rounded-full bg-background-50 px-3 py-1.5 text-[11px] text-foreground-700 transition-colors hover:text-accent-600"
                >
                  #{tag.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-background-200/80 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
              <i className="ri-film-line text-accent-600" />
              推荐影片
            </h2>
            <div className="mt-3 flex flex-col gap-3">
              {recommended.map((movie) => (
                <Link key={movie.id} to={`/detail/${movie.id}`} className="group flex items-center gap-3">
                  <span className="h-[62px] w-[44px] shrink-0 overflow-hidden rounded-sm bg-background-200">
                    <img
                      src={movie.cover}
                      alt={movie.title}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground-800 transition-colors group-hover:text-accent-600">
                      {movie.title}
                    </span>
                    <span className="block text-[11px] text-foreground-400">
                      {movie.year} · {movie.genres.join(" / ")}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-dashed border-background-300 p-5">
            <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground-400">
              <i className="ri-information-line" />
              内容说明
            </p>
            <p className="mt-2 text-xs leading-relaxed text-foreground-500">
              本页资讯均为模拟数据，用于展示影视资讯站点的分类、列表与分页阅读体验。
            </p>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}